import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Form } from "antd";
import toast from "react-hot-toast";
import moment from "moment";
import CryptoJS from "crypto-js";
import { Cookies } from "react-cookie";
import {
  deleteLicenseApi,
  EditLicenseApi,
  GetPlanListApi,
  AddLicenseApi,
  UpgradeLicensePlanApi,
  GetPaymentTypesApi,
} from "../../api/request/license";

import { GetLicenseApi as GetLicenseApiDashboard } from "../../api/request/dashboard";
import { GetCountryDetailsApi } from "../../api/request/countryDetail";
import UserAvatar from "../../components/common/UserAvatar";

const useLicense = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [currentView, setCurrentView] = useState(state?.status || "default");
  const [isLoadingLicense, setIsLoadingLicense] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [previewImage, setPreviewImage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [planOptions, setPlanOptions] = useState([]);
  const [planList, setPlanList] = useState([]);
  const [editingLicense, setEditingLicense] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [upgradeLicenseData, setUpgradeLicenseData] = useState(null);
  const [countryDetails, setCountryDetails] = useState([]);
  const [isLicenseDelete, setIsLicenseDelete] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState();
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isUpgradeLoading, setIsUpgradeLoading] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyLicenseId, setHistoryLicenseId] = useState(null);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const initialValues = { serviceName: "" };
  const secretKey = process.env.REACT_APP_SECRET_KEY;

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    form.setFieldsValue({ logo: newFileList });
    if (!newFileList?.length) {
      form.setFieldsValue({ logo: [] });
    }
  };

  const handleAdminLogin = async (client) => {
    if (!client?.license?.admin_portal_url) return;
    const cookies = new Cookies();
    const { token } = cookies.getAll();
    const data = JSON.stringify({ token, AdminId: client?.id });
    const encrypted = CryptoJS.AES.encrypt(data, secretKey);
    const safeCiphertext = encrypted.toString();

    const redirectUrl = `${
      client?.license?.admin_portal_url
    }super_admin_login?token=${encodeURIComponent(safeCiphertext)}`;
    window.open(redirectUrl);
  };

  const openEditModal = (item) => {
    setEditingLicense(item);
    setIsEditModalOpen(true);
    form.setFieldsValue({
      name: item?.first_name,
      email: item?.email,
      mobile_no: item?.mobile_no,
      domain_name: item?.license?.domain_name,
      license_name: item?.license?.license_name,
      admin_portal_url: item?.license?.admin_portal_url,
      client_portal_url: item?.license?.client_portal_url,
      gst_no: item?.license?.gst_no,
      app_version: item?.license?.app_version,
      is_assign_to_admin: item?.is_assign_to_admin == 1,
      task_assign_to_admin: item?.task_assign_to_admin == 1,
      is_active_placeholder: item?.status === "active",
      payment_types: item?.payment_types || [],
    });
    const logoUrl = item?.license?.logo;
    if (typeof logoUrl === "string" && /^(https?:\/\/|data:)/.test(logoUrl)) {
      const img = new window.Image();
      img.onload = () => {
        setFileList([
          { uid: "-1", name: "logo.png", status: "done", url: logoUrl },
        ]);
      };
      img.onerror = () => {
        setFileList([]);
      };
      img.src = logoUrl;
    } else {
      setFileList([]);
    }
  };

  const openEmployeeModal = (item) => {
    setSelectedEmployee(item);
    setShowEmployeeModal(true);
  };

  const openClientList = (item) => {
    navigate(`/client_list/${item?.id}`, {
      state: { adminData: item },
    });
  };

  const openUpgradeModal = (item) => {
    setUpgradeLicenseData(item);
    setIsUpgradeModalOpen(true);
  };

  const handleUpgradeSubmit = async (planId) => {
    try {
      setIsUpgradeLoading(true);
      const { data } = await UpgradeLicensePlanApi({
        license_id: upgradeLicenseData?.license?.id,
        plan_id: planId,
      });
      if (data?.status) {
        toast.success(data?.message);
        setIsUpgradeModalOpen(false);
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsUpgradeLoading(false);
    }
  };

  const openHistoryModal = (item) => {
    setHistoryLicenseId(item?.license?.id);
    setIsHistoryModalOpen(true);
  };

  // Helper for GetLicenseData to map items
  const mapLicenseData = (item) => ({
    ...item,
    userDetails: <UserAvatar data={item} />,
    businessLogo: (
      <p
        className={`hover:underline ${
          item?.license?.logo
            ? "text-blue-500 cursor-pointer"
            : "cursor-not-allowed text-gray-400"
        }`}
        onClick={() => {
          if (item?.license?.logo) {
            window.open(item.license.logo, "_blank");
          }
        }}
      >
        View
      </p>
    ),

    domainName: item?.license?.domain_name,
    url: (
      <div className="flex flex-col">
        <a
          href={item?.license?.admin_portal_url}
          target="_blank"
          rel="noopener noreferrer"
          className={`${
            item?.license?.admin_portal_url
              ? ""
              : "cursor-not-allowed text-gray-400"
          }`}
        >
          Backoffice
        </a>
        <a
          href={item?.license?.client_portal_url}
          target="_blank"
          rel="noopener noreferrer"
          className={`${
            item?.license?.admin_portal_url
              ? ""
              : "cursor-not-allowed text-gray-400"
          }`}
        >
          Cabinet
        </a>
      </div>
    ),
    licenseName: item?.license?.license_name,
    created_at: item?.created_at
      ? moment(item?.created_at).format("lll")
      : "--",
  });

  const GetLicenseData = async () => {
    try {
      setIsLoadingLicense(true);
      const { data } = await GetLicenseApiDashboard();
      setData(data?.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingLicense(false);
    }
  };

  const deleteItemHandler = async () => {
    try {
      setIsDeleteLoading(true);
      const params = { id: selectedItem?.id };
      const { data } = await deleteLicenseApi(params);
      if (data?.status) {
        toast.success(data?.message);
        setData((prevLicense) =>
          prevLicense?.filter((item) => item?.id !== selectedItem?.id),
        );
        setIsLicenseDelete(false);
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const GetPlanList = async () => {
    try {
      const { data } = await GetPlanListApi();

      if (data?.data) {
        setPlanList(data.data);
        const options = data.data.map((plan) => ({
          label: plan.name,
          value: plan.id,
        }));
        setPlanOptions(options);
      }
    } catch (error) {
      console.error("Error fetching plan list:", error);
    }
  };

  const getPaymentTypes = async () => {
    try {
      const { data } = await GetPaymentTypesApi();
      if (data?.status) {
        setPaymentTypes(data?.data || []);
      }
    } catch (error) {
      console.error("Error fetching payment types:", error);
    }
  };

  const getCountryDetails = async () => {
    try {
      const { data } = await GetCountryDetailsApi();
      if (data?.status) {
        setCountryDetails(data?.data);
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      }
    }
  };

  const buildLicenseFormData = (values, editingLicense) => {
    const formData = new FormData();
    // Common fields for both Add and Edit
    formData.append("domain_name", values.domain_name);
    formData.append("license_name", values.license_name);
    formData.append("admin_portal_url", values.admin_portal_url);
    formData.append("client_portal_url", values.client_portal_url);
    formData.append("gst_no", values.gst_no || "");
    formData.append("app_version", values.app_version || "");
    formData.append("is_assign_to_admin", values?.is_assign_to_admin ? 1 : 0);
    // New Switch field
    formData.append(
      "task_assign_to_admin",
      values.task_assign_to_admin ? 1 : 0,
    );

    if (values.payment_types && values.payment_types.length > 0) {
      values.payment_types.forEach((id) => {
        formData.append("payment_types[]", id);
      });
    } 
    // else {
    //   formData.append("payment_types[]", 0);
    // }

    if (values.logo?.length) {
      const file = values?.logo?.[0]?.originFileObj;
      if (file) {
        formData.append("logo", file);
      }
    }

    if (editingLicense) {
      // Edit mode: only append license_id (and common fields above)
      formData.append("license_id", editingLicense?.license?.id);
    } else {
      // Add mode: append all other fields
      formData.append("name", values.name);
      formData.append("email", values.email);
      formData.append("mobile_no", values.mobile_no);
      formData.append("password", values.password);
      formData.append("plan_id", values.plan_id);
    }

    return formData;
  };

  const handleLicenseSubmit = async (values, isEdit) => {
    try {
      setIsLoading(true);
      const formData = buildLicenseFormData(
        values,
        isEdit ? editingLicense : null,
      );
      const apiHeader = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
      };

      const apiCall = isEdit ? EditLicenseApi : AddLicenseApi;
      const { data } = await apiCall(formData, apiHeader);

      toast.success(
        data?.message ||
          `License ${isEdit ? "updated" : "created"} successfully!`,
      );
      form.resetFields();
      setFileList([]);
      if (isEdit) {
        setEditingLicense(null);
        setIsEditModalOpen(false);
      }
      setCurrentView("default");
      GetLicenseData();
    } catch (error) {
      console.error(`Error ${isEdit ? "editing" : "creating"} license:`, error);
      toast.error(
        error?.response?.data?.message ||
          `Failed to ${isEdit ? "update" : "create"} license!`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddLicense = (values) => handleLicenseSubmit(values, false);
  const handleEditLicense = (values) => handleLicenseSubmit(values, true);

  useEffect(() => {
    GetLicenseData();
    GetPlanList();
    getPaymentTypes();
    getCountryDetails();
  }, []);

  useEffect(() => {
    if (state?.status === "add") {
      setCurrentView("add");
    } else {
      setCurrentView("default");
    }
  }, [state]);

  useEffect(() => {
    if (currentView === "add") {
      form.resetFields();
      setFileList([]);
    }
  }, [currentView, form]);

  return {
    form,
    isLoading,
    data,
    currentView,
    isLoadingLicense,
    fileList,
    previewImage,
    previewOpen,
    planOptions,
    planList,
    editingLicense,
    isEditModalOpen,
    isUpgradeModalOpen,
    setIsUpgradeModalOpen,
    isUpgradeLoading,
    upgradeLicenseData,
    countryDetails,
    isLicenseDelete,
    isDeleteLoading,
    selectedItem,
    showEmployeeModal,
    selectedEmployee,
    initialValues,
    setCurrentView,
    setPreviewOpen,
    setIsEditModalOpen,
    setIsLicenseDelete,
    setShowEmployeeModal,
    setPreviewImage,
    setFileList,
    handlePreview,
    handleChange,
    handleAddLicense,
    handleEditLicense,
    deleteItemHandler,
    handleAdminLogin,
    openEditModal,
    openEmployeeModal,
    openUpgradeModal,
    handleUpgradeSubmit,
    openClientList,
    openHistoryModal,
    isHistoryModalOpen,
    setIsHistoryModalOpen,
    historyLicenseId,
    mapLicenseData,
    setSelectedItem,
    paymentTypes,
  };
};

export default useLicense;
